import React, { useEffect, useState } from 'react'
import { Cookie } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { AppLink as Link } from '@components/AppLink'
import { useStore } from '@store/StoreProvider'
import './CookieConsent.css'

/**
 * One-time cookie notice. Accepting is remembered in localStorage and mirrored into a
 * first-party cookie (so the backend can see it too); the card never comes back after that.
 */
const STORAGE_KEY = 'chatx.cookieConsent'
const COOKIE_NAME = 'chatx_cookie_consent'
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365
const SHOW_DELAY_MS = 700

function hasConsent(): boolean {
  // Cookie first: it is shared across subdomains, localStorage is not.
  try {
    if (document.cookie.split(';').some((part) => part.trim().startsWith(`${COOKIE_NAME}=`))) return true
  } catch {
    /* cookies blocked — fall through */
  }
  try {
    return Boolean(localStorage.getItem(STORAGE_KEY))
  } catch {
    return false
  }
}

/**
 * Registrable domain for the consent cookie (`.chatx.click`), so one "Got it" covers every
 * subdomain — status page, app, landing. localStorage is per-origin and cannot do that.
 * Skipped for localhost / IP hosts, where a Domain attribute would be rejected.
 */
function consentCookieDomain(): string | null {
  const host = window.location.hostname
  if (!host || host === 'localhost' || /^[\d.]+$/.test(host) || host.includes(':')) return null
  const labels = host.split('.').filter(Boolean)
  if (labels.length < 2) return null
  return `.${labels.slice(-2).join('.')}`
}

function rememberConsent(): void {
  const stamp = new Date().toISOString()
  try {
    localStorage.setItem(STORAGE_KEY, stamp)
  } catch {
    /* ignore */
  }
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : ''
    const domain = consentCookieDomain()
    const domainAttr = domain ? `; Domain=${domain}` : ''
    document.cookie = `${COOKIE_NAME}=1; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax${domainAttr}${secure}`
  } catch {
    /* ignore */
  }
}

/** Phones get a one-line version: the chat launcher and its greeting sit right above the bar. */
function useCompactViewport(): boolean {
  const query = '(max-width: 639px)'
  const [compact, setCompact] = useState(() => (typeof window !== 'undefined' ? window.matchMedia(query).matches : false))
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mql = window.matchMedia(query)
    const onChange = () => setCompact(mql.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])
  return compact
}

export const CookieConsent: React.FC = observer(() => {
  const { i18n } = useStore()
  const [visible, setVisible] = useState(false)
  const compact = useCompactViewport()

  useEffect(() => {
    if (typeof window === 'undefined' || hasConsent()) return
    const timer = window.setTimeout(() => setVisible(true), SHOW_DELAY_MS)
    return () => window.clearTimeout(timer)
  }, [])

  if (!visible) return null

  const ru = i18n.language === 'ru'
  const tx = (key: string, fallbackRu: string, fallbackEn: string) => {
    const value = i18n.t(key)
    return value === key ? (ru ? fallbackRu : fallbackEn) : value
  }

  const accept = () => {
    rememberConsent()
    setVisible(false)
  }

  return (
    <div className="cookie-consent" role="dialog" aria-live="polite" aria-label={tx('cookies.title', 'Cookies', 'Cookies')}>
      <div className="cookie-consent__inner">
        <div className="cookie-consent__icon" aria-hidden="true">
          <Cookie size={18} strokeWidth={2} />
        </div>
        <div className="cookie-consent__body">
          <p className="cookie-consent__text">
            {compact
              ? tx('cookies.text_short', 'Используем cookies для входа и настроек.', 'We use cookies for sign-in and settings.')
              : tx(
                  'cookies.text',
                  'Сайт использует cookies — они нужны для входа и сохранения настроек. Продолжая, вы соглашаетесь с',
                  'This site uses cookies — they keep you signed in and remember your settings. By continuing you agree to the',
                )}{' '}
            <Link to="/policy" className="cookie-consent__link">
              {compact
                ? tx('cookies.policy_short', 'Политика', 'Policy')
                : tx('cookies.policy', 'политикой конфиденциальности', 'privacy policy')}
            </Link>
            {compact ? '' : '.'}
          </p>
          <button type="button" className="cookie-consent__btn" onClick={accept}>
            {tx('cookies.accept', 'Понятно', 'Got it')}
          </button>
        </div>
      </div>
    </div>
  )
})

export default CookieConsent
